import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, Plus, Trash2, CheckCircle2, X } from 'lucide-react';
import { useCreatePackageMutation, useUpdatePackageMutation } from '../../../redux/actions/Admin/ServiceAdmin';
import { IServicePackageRequest, IPackagePricing } from '../../../types/Request/AdminRequest';

interface PackageFormScreenProps {
  mode: 'add' | 'edit';
  serviceId: number;
  initialData: any | null;
  onClose: () => void;
}

export default function PackageFormScreen({ mode, serviceId, initialData, onClose }: PackageFormScreenProps) {
  const [createPackage, { isLoading: isCreating }] = useCreatePackageMutation();
  const [updatePackage, { isLoading: isUpdating }] = useUpdatePackageMutation();
  const isProcessing = isCreating || isUpdating;

  const [formData, setFormData] = useState<IServicePackageRequest & { includedBookings?: number }>({
    serviceId: serviceId,
    packageTitle: '',
    packageSubtitle: '',
    description: '',
    durationMinutes: 45,
    capacity: 0,
    isActive: true,
    bufferMinutes: 15,
    validForMonths: 0,
    includedBookings: 0,
    validFor: ['Dog', 'Cat'],
    includes: [],
    pricing: []
  });

  const [newInclusion, setNewInclusion] = useState('');
  
  const [pricingStager, setPricingStager] = useState<IPackagePricing>({
    petType: 'Dog',
    petSize: 'Medium',
    label: '',
    originalPrice: 0,
    salePrice: 0
  });

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData({
        id: initialData.id,
        serviceId: initialData.serviceId || serviceId,
        packageTitle: initialData.packageTitle || '',
        packageSubtitle: initialData.packageSubtitle || '',
        description: initialData.description || '',
        durationMinutes: initialData.durationMinutes || 0,
        bufferMinutes: initialData.bufferMinutes || 0,
        validForMonths: initialData.validForMonths || 0,
        includedBookings: initialData.includedBookings || 0,
        validFor: initialData.validFor || [],
        includes: initialData.includes || [],
        pricing: initialData.pricing || [],
        isActive: true,
      });
    }
  }, [mode, initialData, serviceId]);

  const addInclusion = () => {
    if (!newInclusion.trim()) return;
    setFormData(prev => ({ ...prev, includes: [...prev.includes, newInclusion.trim()] }));
    setNewInclusion('');
  };

  const removeInclusion = (index: number) => {
    setFormData(prev => ({ ...prev, includes: prev.includes.filter((_, i) => i !== index) }));
  };

  const addPricingTier = () => {
    if (pricingStager.originalPrice <= 0) return;
    
    const computedLabel = pricingStager.label.trim() || `${pricingStager.petType} - ${pricingStager.petSize}`;
    const finalizedTier: IPackagePricing = {
      ...pricingStager,
      label: computedLabel
    };

    setFormData(prev => ({ ...prev, pricing: [...prev.pricing, finalizedTier] }));
    setPricingStager({ petType: 'Dog', petSize: 'Medium', label: '', originalPrice: 0, salePrice: 0 });
  };

  const removePricingTier = (index: number) => {
    setFormData(prev => ({ ...prev, pricing: prev.pricing.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.pricing.length === 0) {
      alert("Please add at least one pricing option.");
      return;
    }

    try {
      if (mode === 'edit') {
        await updatePackage(formData).unwrap();
      } else {
        await createPackage(formData).unwrap();
      }
      onClose();
    } catch (err) {
      console.error("Failed to save package:", err);
    }
  };

  return (
    <div className="min-h-screen px-4 py-6 flex items-center justify-center w-full">
      <div className="max-w-3xl w-full space-y-4 md:space-y-6">
        
        {/* Back Button */}
        <button
          type="button"
          onClick={onClose}
          disabled={isProcessing}
          className="inline-flex items-center gap-2 text-xs md:text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors cursor-pointer group disabled:opacity-50"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform stroke-[2.5]" />
          Back to Packages
        </button>

        {/* Form Container */}
        <div className="bg-white rounded-2xl md:rounded-3xl shadow-md border border-gray-200 overflow-hidden">
          
          <div className="bg-white p-5 md:p-6 border-b border-gray-100">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 tracking-tight">
              {mode === 'edit' ? 'Edit Package' : 'Create New Package'}
            </h2>
            <p className="text-xs font-medium text-gray-500 mt-1">
              Set package details, features, and pricing options.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-5 md:space-y-6">
            
            {/* Title & Subtitle */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">Package Title</label>
                <input
                  type="text" required disabled={isProcessing}
                  placeholder="e.g., Ultra Spa Care Premium"
                  value={formData.packageTitle}
                  onChange={(e) => setFormData({ ...formData, packageTitle: e.target.value })}
                  className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm font-medium transition focus:border-green-500 outline-none focus:bg-white"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">Package Subtitle</label>
                <input
                  type="text" required disabled={isProcessing}
                  placeholder="e.g., Best Value For Large Dogs"
                  value={formData.packageSubtitle}
                  onChange={(e) => setFormData({ ...formData, packageSubtitle: e.target.value })}
                  className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm font-medium transition focus:border-green-500 outline-none focus:bg-white"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-600">Description</label>
              <textarea
                required rows={2} disabled={isProcessing}
                placeholder="Brief summary of what is included in this package..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm font-medium transition focus:border-green-500 outline-none focus:bg-white resize-none"
              />
            </div>

            {/* Duration, Buffer, Validity & Included Bookings Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">Duration (Mins)</label>
                <input
                  type="number" required min={1} disabled={isProcessing}
                  value={formData.durationMinutes}
                  onChange={(e) => setFormData({ ...formData, durationMinutes: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm font-medium transition focus:border-green-500 outline-none focus:bg-white"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">Buffer Time (Mins)</label>
                <input
                  type="number" required min={0} disabled={isProcessing}
                  value={formData.bufferMinutes}
                  onChange={(e) => setFormData({ ...formData, bufferMinutes: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm font-medium transition focus:border-green-500 outline-none focus:bg-white"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">Validity (Months)</label>
                <input
                  type="number" required min={0} disabled={isProcessing}
                  value={formData.validForMonths}
                  onChange={(e) => setFormData({ ...formData, validForMonths: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm font-medium transition focus:border-green-500 outline-none focus:bg-white"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">Included Bookings</label>
                <input
                  type="number" required min={0} disabled={isProcessing}
                  placeholder="0"
                  value={formData.includedBookings}
                  onChange={(e) => setFormData({ ...formData, includedBookings: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm font-medium transition focus:border-green-500 outline-none focus:bg-white"
                />
              </div>
            </div>

            {/* Inclusions */}
            <div className="space-y-2 border-t border-gray-100 pt-4">
              <label className="text-xs font-semibold text-gray-600">Package Features / What's Included</label>
              <div className="flex flex-row gap-2">
                <input
                  type="text" disabled={isProcessing}
                  placeholder="e.g., Conditioning Treatment"
                  value={newInclusion}
                  onChange={(e) => setNewInclusion(e.target.value)}
                  className="flex-1 min-w-0 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium transition focus:border-green-500 outline-none"
                />
                <button
                  type="button" onClick={addInclusion} disabled={isProcessing}
                  className="bg-gray-800 text-white px-4 rounded-xl text-xs font-bold uppercase tracking-wide cursor-pointer hover:bg-black transition shrink-0"
                >
                  Add Feature
                </button>
              </div>
              
              <div className="flex flex-wrap gap-1.5 mt-2 max-h-[100px] overflow-y-auto pr-1">
                {formData.includes.map((inc, index) => (
                  <span key={index} className="inline-flex items-center gap-1.5 text-xs bg-gray-100 border border-gray-200 text-gray-700 px-3 py-1 rounded-lg font-medium">
                    {inc}
                    <X className="w-3.5 h-3.5 cursor-pointer text-gray-400 hover:text-red-500" onClick={() => removeInclusion(index)} />
                  </span>
                ))}
              </div>
            </div>

            {/* Pricing Section */}
            <div className="space-y-3 border-t border-gray-100 pt-4 bg-gray-50 p-4 rounded-2xl border border-gray-200">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">Add Pricing Option</p>
              
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-gray-500">Pet Type</span>
                  <select 
                    value={pricingStager.petType} 
                    onChange={(e) => setPricingStager({ ...pricingStager, petType: e.target.value })}
                    className="w-full border border-gray-200 bg-white rounded-xl p-2.5 text-xs font-medium outline-none cursor-pointer focus:border-green-500"
                  >
                    <option value="Dog">Dog</option>
                    <option value="Cat">Cat</option>
                    <option value="Bird">Bird</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <span className="text-xs font-semibold text-gray-500">Pet Size</span>
                  <select 
                    value={pricingStager.petSize} 
                    onChange={(e) => setPricingStager({ ...pricingStager, petSize: e.target.value })}
                    className="w-full border border-gray-200 bg-white rounded-xl p-2.5 text-xs font-medium outline-none cursor-pointer focus:border-green-500"
                  >
                    <option value="Small">Small</option>
                    <option value="Medium">Medium</option>
                    <option value="Large">Large</option>
                    <option value="X-Large">Extra Large</option>
                  </select>
                </div>

                <div className="col-span-2 md:col-span-1 space-y-1">
                  <span className="text-xs font-semibold text-gray-500">Option Label</span>
                  <input
                    type="text" placeholder="e.g., Medium Dog Rate"
                    value={pricingStager.label}
                    onChange={(e) => setPricingStager({ ...pricingStager, label: e.target.value })}
                    className="w-full border border-gray-200 bg-white rounded-xl p-2.5 text-xs font-medium outline-none focus:border-green-500"
                  />
                </div>

                <div className="space-y-1">
                  <span className="text-xs font-semibold text-gray-500">Price (AED)</span>
                  <input
                    type="number" min={0}
                    value={pricingStager.originalPrice || ''}
                    onChange={(e) => setPricingStager({ ...pricingStager, originalPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full border border-gray-200 bg-white rounded-xl p-2.5 text-xs font-medium outline-none focus:border-green-500"
                  />
                </div>

                <div className="flex items-end col-span-2 md:col-span-1">
                  <button
                    type="button" onClick={addPricingTier}
                    className="w-full bg-green-600 hover:bg-green-700 text-white p-2.5 rounded-xl text-xs font-bold transition cursor-pointer text-center"
                  >
                    Add Price
                  </button>
                </div>
              </div>

              {/* Pricing List */}
              {formData.pricing.length > 0 && (
                <div className="mt-3 border border-gray-200 bg-white rounded-xl overflow-hidden divide-y divide-gray-100 max-h-[160px] overflow-y-auto">
                  {formData.pricing.map((tier, index) => (
                    <div key={index} className="p-3 flex items-center justify-between gap-2 text-xs font-medium text-gray-700 hover:bg-gray-50">
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                        <span className="truncate">{tier.label} <span className="text-xs font-normal text-gray-400">({tier.petType}/{tier.petSize})</span></span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-gray-900 font-bold">AED {tier.originalPrice}</span>
                        <button type="button" onClick={() => removePricingTier(index)} className="text-gray-400 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-end gap-2 pt-4 border-t border-gray-100">
              <button
                type="button" onClick={onClose} disabled={isProcessing}
                className="w-full sm:w-auto text-center border border-gray-300 bg-white px-5 py-2.5 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              
              <button
                type="submit" disabled={isProcessing}
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-xs transition inline-flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {isProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === 'edit' ? 'Update Package' : 'Save Package'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}